import {
  BRAND_CLIENT_NAME,
  SPEC_MAP_COMMENTS,
  SPEC_MAP_LANES,
  SPEC_MAP_METADATA,
  SPEC_MAP_NODES,
  SPEC_MAP_VERSIONS,
  TEAM_ROSTER,
} from '@/app/data/specmap';
import { getSupabaseBrowserClient } from '@/lib/supabase/client';
import { computeLaneCompletion, computeMetrics } from './metrics';
import {
  ActivityDatum,
  ActivityItem,
  ChildScreen,
  CollaboratorData,
  CommentData,
  CreateDependencyInput,
  CreateScreenInput,
  Dependency,
  LanePatch,
  LaneData,
  NodeData,
  NodePatch,
  NotificationData,
  ShareLinkData,
  SpecMapData,
  SpecMapExport,
  SpecMapMetadataPatch,
  SpecMapState,
  SpecMapVersion,
} from './types';

export const DEFAULT_SPEC_MAP_ID = SPEC_MAP_METADATA.id;
export const MAX_LOGO_FILE_SIZE = 2 * 1024 * 1024;
export const LOGO_STORAGE_BUCKET = 'spec-map-assets';

const SUPPORTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

// Bumped to v2 because the seed shape changed (JSON-driven, excluded nodes,
// reset progress) — old persisted localStorage entries are no longer valid.
const STORAGE_KEY = `specmap:${BRAND_CLIENT_NAME}:state:v2`;
const CURRENT_USER = {
  id: 'local-user-thenu',
  name: TEAM_ROSTER.engineer2.name,
};

function makeId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function nowIso() {
  return new Date().toISOString();
}

function toDateLabel(value: string) {
  if (!value) {
    return new Date().toISOString().slice(0, 10);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toISOString().slice(0, 10);
}

function normalizeSpecMapData(specMap: Partial<SpecMapData>, fallback: SpecMapData): SpecMapData {
  return {
    ...fallback,
    ...specMap,
    id: specMap.id || fallback.id,
    title: specMap.title || fallback.title,
    description: specMap.description ?? fallback.description,
    status: specMap.status || fallback.status,
    activeVersionId: specMap.activeVersionId || fallback.activeVersionId,
    ownerName: specMap.ownerName || fallback.ownerName,
    clientName: specMap.clientName ?? fallback.clientName ?? '',
    logoUrl: specMap.logoUrl ?? fallback.logoUrl ?? null,
    logoAlt:
      specMap.logoAlt ??
      `${specMap.clientName || specMap.title || fallback.title} logo`,
    createdAt: specMap.createdAt || fallback.createdAt,
    updatedAt: specMap.updatedAt || fallback.updatedAt,
  };
}

function formatTimeAgo(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60_000));

  if (minutes < 1) {
    return 'just now';
  }

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  if (days < 14) {
    return `${days}d ago`;
  }

  return toDateLabel(value);
}

export function validateLogoFile(file: Pick<File, 'type' | 'size' | 'name'>) {
  if (!SUPPORTED_LOGO_TYPES.includes(file.type)) {
    throw new Error('Logo must be a PNG, JPG, WebP, or SVG file.');
  }

  if (file.size > MAX_LOGO_FILE_SIZE) {
    throw new Error('Logo must be 2 MB or smaller.');
  }

  if (!file.name.trim()) {
    throw new Error('Logo file name is required.');
  }
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read logo file.'));
    reader.readAsDataURL(file);
  });
}

function logoAltFromFile(file: File, state: SpecMapState) {
  const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
  return baseName || state.specMap.clientName || state.specMap.title || 'Client logo';
}

function lastSavedLabel(nodes: NodeData[], comments: CommentData[], specMap: SpecMapData) {
  const timestamps = [
    specMap.updatedAt,
    ...nodes.map((node) => node.updatedAt),
    ...comments.map((comment) => comment.createdAt),
  ]
    .map((value) => new Date(value).getTime())
    .filter((value) => !Number.isNaN(value));

  if (timestamps.length === 0) {
    return 'Not saved yet';
  }

  return formatTimeAgo(new Date(Math.max(...timestamps)).toISOString());
}

function buildActivitySeries(nodes: NodeData[]) {
  const today = new Date();
  const days = Array.from({ length: 14 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (13 - index));
    return date;
  });

  return nodes.reduce<Record<string, ActivityDatum[]>>((series, node) => {
    series[node.id] = days.map((day) => {
      const dayKey = day.toISOString().slice(0, 10);
      const count = node.activity.filter((item) => {
        const itemDate = item.createdAt ? toDateLabel(item.createdAt) : '';
        return itemDate === dayKey;
      }).length;

      return {
        day: day.toLocaleDateString('en', { month: 'short', day: 'numeric' }),
        count,
      };
    });

    return series;
  }, {});
}

function deriveNotifications(nodes: NodeData[]) {
  const notifications: NotificationData[] = [];

  nodes.forEach((node) => {
    if (node.status === 'Deprecated') {
      notifications.push({
        id: `derived-deprecated-${node.id}`,
        title: 'Deprecated item',
        body: `${node.title} is deprecated and should be reviewed before new links are added.`,
        type: 'dependency',
        read: false,
        nodeId: node.id,
        createdAt: node.updatedAt,
      });
    }

    if (node.priority === 'High' && ['Draft', 'In Review'].includes(node.status)) {
      notifications.push({
        id: `derived-review-${node.id}`,
        title: 'Review needed',
        body: `${node.title} is high priority and still ${node.status.toLowerCase()}.`,
        type: 'review',
        read: false,
        nodeId: node.id,
        createdAt: node.updatedAt,
      });
    }
  });

  return notifications;
}

function dedupeNotifications(notifications: NotificationData[]) {
  const byId = new Map<string, NotificationData>();

  notifications.forEach((notification) => {
    byId.set(notification.id, notification);
  });

  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function finalizeState(state: SpecMapState): SpecMapState {
  const lanes = state.lanes
    .map((lane) => ({
      ...lane,
      completion: computeLaneCompletion(lane, state.nodes),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const nodes = state.nodes
    .map((node) => ({
      ...node,
      screenCount: node.childScreens.length,
      childScreens: [...node.childScreens].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const notifications = dedupeNotifications([
    ...state.notifications,
    ...deriveNotifications(nodes),
  ]);

  return {
    ...state,
    lanes,
    nodes,
    notifications,
    metrics: computeMetrics(nodes, lanes),
    activitySeries: buildActivitySeries(nodes),
    lastSavedLabel: lastSavedLabel(nodes, state.comments, state.specMap),
  };
}

function buildSeedNodes(): NodeData[] {
  return SPEC_MAP_NODES.map((node, index) => ({
    ...node,
    sortOrder: node.sortOrder ?? index,
    childScreens: node.childScreens.map((screen, screenIndex) => ({
      ...screen,
      order: screen.order ?? screenIndex,
    })),
    activity: node.activity.map((activity, activityIndex) => ({
      ...activity,
      createdAt:
        activity.createdAt ??
        new Date(Date.now() - (activityIndex + 1) * 3_600_000).toISOString(),
    })),
  }));
}

function buildSeedState(source: 'local' | 'supabase' = 'local'): SpecMapState {
  const nodes = buildSeedNodes();
  const lanes: LaneData[] = SPEC_MAP_LANES.map((lane, index) => ({
    ...lane,
    order: lane.order ?? index,
  }));
  const specMap: SpecMapData = { ...SPEC_MAP_METADATA };
  const versions: SpecMapVersion[] = SPEC_MAP_VERSIONS.map((version) => ({ ...version }));

  return finalizeState({
    specMap,
    versions,
    lanes,
    nodes,
    metrics: [],
    activitySeries: {},
    comments: SPEC_MAP_COMMENTS.map((comment) => ({ ...comment })),
    notifications: [],
    collaborators: [
      {
        id: 'collab-tech-lead',
        name: TEAM_ROSTER.techLead.name,
        initials: TEAM_ROSTER.techLead.initials,
        color: TEAM_ROSTER.techLead.color,
        role: TEAM_ROSTER.techLead.role,
        active: true,
      },
      {
        id: 'collab-engineer-1',
        name: TEAM_ROSTER.engineer1.name,
        initials: TEAM_ROSTER.engineer1.initials,
        color: TEAM_ROSTER.engineer1.color,
        role: TEAM_ROSTER.engineer1.role,
        active: true,
      },
      {
        id: 'collab-engineer-2',
        name: TEAM_ROSTER.engineer2.name,
        initials: TEAM_ROSTER.engineer2.initials,
        color: TEAM_ROSTER.engineer2.color,
        role: TEAM_ROSTER.engineer2.role,
        active: true,
      },
      {
        id: 'collab-project-manager',
        name: TEAM_ROSTER.projectManager.name,
        initials: TEAM_ROSTER.projectManager.initials,
        color: TEAM_ROSTER.projectManager.color,
        role: TEAM_ROSTER.projectManager.role,
        active: true,
      },
    ],
    shareLinks: [],
    source,
    lastSavedLabel: 'Not saved yet',
  });
}

function readLocalState() {
  if (typeof window === 'undefined') {
    return buildSeedState('local');
  }

  const rawState = window.localStorage.getItem(STORAGE_KEY);

  if (!rawState) {
    const seed = buildSeedState('local');
    writeLocalState(seed);
    return seed;
  }

  try {
    const parsed = JSON.parse(rawState) as SpecMapState;
    const seed = buildSeedState('local');
    return finalizeState({
      ...seed,
      ...parsed,
      specMap: normalizeSpecMapData(parsed.specMap ?? {}, seed.specMap),
      source: 'local',
    });
  } catch (_error) {
    const seed = buildSeedState('local');
    writeLocalState(seed);
    return seed;
  }
}

function writeLocalState(state: SpecMapState) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(finalizeState(state)));
}

function mutateLocalState(mutator: (state: SpecMapState) => void) {
  const state = readLocalState();
  mutator(state);
  state.specMap.updatedAt = nowIso();
  const nextState = finalizeState(state);
  writeLocalState(nextState);
  return nextState;
}

function addLocalActivity(
  state: SpecMapState,
  nodeId: string | null,
  type: ActivityItem['type'],
  message: string
) {
  const activity: ActivityItem = {
    id: makeId('act'),
    user: CURRENT_USER.name,
    time: 'just now',
    message,
    type,
    createdAt: nowIso(),
  };

  if (nodeId) {
    const node = state.nodes.find((item) => item.id === nodeId);

    if (node) {
      node.activity = [activity, ...node.activity];
      node.updatedAt = activity.createdAt ?? nowIso();
    }
  }
}

function asStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

async function readSupabaseState(specMapId: string): Promise<SpecMapState> {
  const supabase = getSupabaseBrowserClient();

  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  const { data: mapRow, error: mapError } = await supabase
    .from('spec_maps')
    .select('*')
    .eq('id', specMapId)
    .single();

  if (mapError || !mapRow) {
    throw mapError ?? new Error('Spec map not found.');
  }

  const activeVersionId = mapRow.active_version_id ?? '';

  const [
    { data: versionRows, error: versionError },
    { data: laneRows, error: laneError },
    { data: nodeRows, error: nodeError },
    { data: commentRows, error: commentError },
    { data: notificationRows, error: notificationError },
    { data: shareRows, error: shareError },
  ] = await Promise.all([
    supabase.from('spec_map_versions').select('*').eq('spec_map_id', specMapId).order('created_at'),
    supabase
      .from('lanes')
      .select('*')
      .eq('spec_map_id', specMapId)
      .eq('version_id', activeVersionId)
      .order('sort_order'),
    supabase
      .from('nodes')
      .select('*')
      .eq('spec_map_id', specMapId)
      .eq('version_id', activeVersionId)
      .order('sort_order'),
    supabase.from('comments').select('*').eq('spec_map_id', specMapId).order('created_at'),
    supabase
      .from('notifications')
      .select('*')
      .eq('spec_map_id', specMapId)
      .order('created_at'),
    supabase.from('share_links').select('*').eq('spec_map_id', specMapId).order('created_at'),
  ]);

  const loadError = versionError || laneError || nodeError || commentError || notificationError || shareError;

  if (loadError) {
    throw loadError;
  }

  if (!nodeRows || nodeRows.length === 0) {
    throw new Error('No Supabase nodes found. Run the SpecMap migration and seed data first.');
  }

  const nodeIds = nodeRows.map((node) => node.id);
  const [
    { data: screenRows, error: screenError },
    { data: dependencyRows, error: dependencyError },
    { data: activityRows, error: activityError },
  ] = await Promise.all([
    supabase.from('screens').select('*').in('node_id', nodeIds).order('sort_order'),
    supabase.from('dependencies').select('*').in('source_node_id', nodeIds).order('created_at'),
    supabase
      .from('activity_events')
      .select('*')
      .eq('spec_map_id', specMapId)
      .order('created_at', { ascending: false })
      .limit(250),
  ]);

  const relationError = screenError || dependencyError || activityError;

  if (relationError) {
    throw relationError;
  }

  const screensByNode = new Map<string, ChildScreen[]>();
  const dependenciesByNode = new Map<string, Dependency[]>();
  const activityByNode = new Map<string, ActivityItem[]>();

  screenRows?.forEach((screen) => {
    const screens = screensByNode.get(screen.node_id) ?? [];
    screens.push({
      id: screen.id,
      label: screen.label,
      status: screen.status,
      priority: screen.priority,
      owner: screen.owner_name,
      description: screen.description ?? '',
      order: screen.sort_order,
    });
    screensByNode.set(screen.node_id, screens);
  });

  dependencyRows?.forEach((dependency) => {
    const dependencies = dependenciesByNode.get(dependency.source_node_id) ?? [];
    dependencies.push({
      id: dependency.id,
      label: dependency.label,
      status: dependency.status,
      targetNodeId: dependency.target_node_id,
      targetExternalId: dependency.target_external_id,
    });
    dependenciesByNode.set(dependency.source_node_id, dependencies);
  });

  activityRows?.forEach((activity) => {
    if (!activity.node_id) {
      return;
    }

    const items = activityByNode.get(activity.node_id) ?? [];
    items.push({
      id: activity.id,
      user: activity.actor_name,
      time: formatTimeAgo(activity.created_at),
      message: activity.message,
      type: activity.type as ActivityItem['type'],
      createdAt: activity.created_at,
    });
    activityByNode.set(activity.node_id, items);
  });

  const specMap: SpecMapData = {
    id: mapRow.id,
    title: mapRow.title,
    description: mapRow.description ?? '',
    status: mapRow.status,
    activeVersionId,
    ownerName: SPEC_MAP_METADATA.ownerName,
    clientName: mapRow.client_name ?? '',
    logoUrl: mapRow.logo_url ?? null,
    logoAlt: mapRow.logo_alt ?? `${mapRow.title} logo`,
    createdAt: mapRow.created_at,
    updatedAt: mapRow.updated_at,
  };
  const lanes: LaneData[] =
    laneRows?.map((lane) => ({
      id: lane.id,
      label: lane.label,
      description: lane.description ?? '',
      completion: lane.completion,
      color: lane.color,
      order: lane.sort_order,
    })) ?? [];
  const nodes: NodeData[] = nodeRows.map((node) => {
    const childScreens = screensByNode.get(node.id) ?? [];

    return {
      id: node.id,
      title: node.title,
      description: node.description ?? '',
      category: node.category,
      status: node.status,
      priority: node.priority,
      owner: node.owner_name,
      lane: node.lane_id,
      screenCount: childScreens.length,
      childScreens,
      dependencies: dependenciesByNode.get(node.id) ?? [],
      relatedScreens: asStringArray(node.related_screens),
      notes: node.notes ?? '',
      createdAt: node.created_at,
      updatedAt: node.updated_at,
      activity: activityByNode.get(node.id) ?? [],
      positionX: node.position_x,
      positionY: node.position_y,
      sortOrder: node.sort_order,
      archivedAt: node.archived_at,
    };
  });

  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  return finalizeState({
    specMap,
    versions:
      versionRows?.map((version) => ({
        id: version.id,
        label: version.label,
        status: version.status,
        isCurrent: version.is_current,
        createdAt: version.created_at,
      })) ?? [],
    lanes,
    nodes,
    metrics: [],
    activitySeries: {},
    comments:
      commentRows?.map((comment) => ({
        id: comment.id,
        nodeId: comment.node_id,
        user: comment.user_name,
        message: comment.message,
        createdAt: comment.created_at,
      })) ?? [],
    notifications:
      notificationRows?.map((notification) => ({
        id: notification.id,
        title: notification.title,
        body: notification.body,
        type: notification.type as NotificationData['type'],
        read: notification.read,
        createdAt: notification.created_at,
        nodeId: notification.node_id,
      })) ?? [],
    collaborators: buildSeedState('supabase').collaborators,
    shareLinks:
      shareRows?.map((share) => ({
        id: share.id,
        token: share.token,
        permission: share.permission as ShareLinkData['permission'],
        createdAt: share.created_at,
        url: `${currentOrigin}/share/${share.token}`,
      })) ?? [],
    source: 'supabase',
    lastSavedLabel: '',
  });
}

async function insertSupabaseActivity(
  specMapId: string,
  versionId: string,
  nodeId: string | null,
  type: ActivityItem['type'],
  message: string
) {
  const supabase = getSupabaseBrowserClient();

  if (!supabase) {
    return;
  }

  const { data } = await supabase.auth.getUser();
  const user = data.user;
  const actorName =
    (user?.user_metadata?.full_name as string | undefined) ?? user?.email ?? CURRENT_USER.name;

  await supabase.from('activity_events').insert({
    spec_map_id: specMapId,
    version_id: versionId,
    node_id: nodeId,
    actor_id: user?.id ?? null,
    actor_name: actorName,
    type,
    message,
  });
}

function nodePatchToSupabaseUpdate(patch: NodePatch) {
  return {
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.description !== undefined ? { description: patch.description } : {}),
    ...(patch.category !== undefined ? { category: patch.category } : {}),
    ...(patch.status !== undefined ? { status: patch.status } : {}),
    ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
    ...(patch.owner !== undefined ? { owner_name: patch.owner } : {}),
    ...(patch.lane !== undefined ? { lane_id: patch.lane } : {}),
    ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
    ...(patch.positionX !== undefined ? { position_x: patch.positionX } : {}),
    ...(patch.positionY !== undefined ? { position_y: patch.positionY } : {}),
    ...(patch.sortOrder !== undefined ? { sort_order: patch.sortOrder } : {}),
    updated_at: nowIso(),
  };
}

function describeNodePatch(patch: NodePatch) {
  if (patch.status) {
    return `Changed status to ${patch.status}.`;
  }

  if (patch.priority) {
    return `Changed priority to ${patch.priority}.`;
  }

  if (patch.notes !== undefined) {
    return 'Updated notes.';
  }

  if (patch.title || patch.description || patch.category || patch.owner || patch.lane) {
    return 'Updated node details.';
  }

  return '';
}

function lanePatchToSupabaseUpdate(patch: LanePatch) {
  return {
    ...(patch.label !== undefined ? { label: patch.label } : {}),
    ...(patch.description !== undefined ? { description: patch.description } : {}),
    ...(patch.color !== undefined ? { color: patch.color } : {}),
    updated_at: nowIso(),
  };
}

export async function getSpecMapState(specMapId = DEFAULT_SPEC_MAP_ID) {
  try {
    return await readSupabaseState(specMapId);
  } catch (_error) {
    return readLocalState();
  }
}

export async function switchVersion(specMapId: string, versionId: string) {
  const supabase = getSupabaseBrowserClient();

  if (supabase) {
    try {
      await supabase.from('spec_map_versions').update({ is_current: false }).eq('spec_map_id', specMapId);
      await supabase.from('spec_map_versions').update({ is_current: true }).eq('id', versionId);
      await supabase
        .from('spec_maps')
        .update({ active_version_id: versionId, updated_at: nowIso() })
        .eq('id', specMapId);
      return await getSpecMapState(specMapId);
    } catch (_error) {
      return mutateLocalState((state) => {
        state.specMap.activeVersionId = versionId;
        state.versions = state.versions.map((version) => ({
          ...version,
          isCurrent: version.id === versionId,
          status: version.id === versionId ? 'Current' : version.status === 'Current' ? 'Archived' : version.status,
        }));
      });
    }
  }

  return mutateLocalState((state) => {
    state.specMap.activeVersionId = versionId;
    state.versions = state.versions.map((version) => ({
      ...version,
      isCurrent: version.id === versionId,
      status: version.id === versionId ? 'Current' : version.status === 'Current' ? 'Archived' : version.status,
    }));
  });
}

export async function updateNode(specMapId: string, nodeId: string, patch: NodePatch) {
  const currentState = await getSpecMapState(specMapId);
  const message = describeNodePatch(patch);
  const isPositionOnly =
    Object.keys(patch).every((key) => ['positionX', 'positionY', 'sortOrder'].includes(key)) &&
    Object.keys(patch).length > 0;

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('nodes').update(nodePatchToSupabaseUpdate(patch)).eq('id', nodeId);

      if (message && !isPositionOnly) {
        await insertSupabaseActivity(
          specMapId,
          currentState.specMap.activeVersionId,
          nodeId,
          patch.status ? 'status' : 'edit',
          message
        );
      }

      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const node = state.nodes.find((item) => item.id === nodeId);

    if (!node) {
      throw new Error('Node not found.');
    }

    Object.assign(node, patch, { updatedAt: nowIso() });

    if (message && !isPositionOnly) {
      addLocalActivity(state, nodeId, patch.status ? 'status' : 'edit', message);
    }
  });
}

export async function updateLane(specMapId: string, laneId: string, patch: LanePatch) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('lanes').update(lanePatchToSupabaseUpdate(patch)).eq('id', laneId);
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        null,
        'edit',
        'Updated lane details.'
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const lane = state.lanes.find((item) => item.id === laneId);

    if (!lane) {
      throw new Error('Lane not found.');
    }

    Object.assign(lane, patch);
    addLocalActivity(state, null, 'edit', 'Updated lane details.');
  });
}

export async function createComment(specMapId: string, nodeId: string, message: string) {
  const trimmed = message.trim();

  if (!trimmed) {
    throw new Error('Comment cannot be empty.');
  }

  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      const { data } = await supabase.auth.getUser();
      const userName =
        (data.user?.user_metadata?.full_name as string | undefined) ??
        data.user?.email ??
        CURRENT_USER.name;

      await supabase.from('comments').insert({
        spec_map_id: specMapId,
        node_id: nodeId,
        user_id: data.user?.id ?? null,
        user_name: userName,
        message: trimmed,
      });
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        nodeId,
        'comment',
        trimmed
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const comment: CommentData = {
      id: makeId('comment'),
      nodeId,
      user: CURRENT_USER.name,
      message: trimmed,
      createdAt: nowIso(),
    };
    state.comments = [comment, ...state.comments];
    addLocalActivity(state, nodeId, 'comment', trimmed);
  });
}

export async function createDependency(specMapId: string, input: CreateDependencyInput) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('dependencies').insert({
        spec_map_id: specMapId,
        version_id: currentState.specMap.activeVersionId,
        source_node_id: input.sourceNodeId,
        target_node_id: input.targetNodeId ?? null,
        label: input.label,
        status: input.status,
      });
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        input.sourceNodeId,
        'link',
        `Linked dependency: ${input.label}.`
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const node = state.nodes.find((item) => item.id === input.sourceNodeId);

    if (!node) {
      throw new Error('Node not found.');
    }

    node.dependencies = [
      ...node.dependencies,
      {
        id: makeId('dep'),
        label: input.label,
        status: input.status,
        targetNodeId: input.targetNodeId ?? null,
      },
    ];
    addLocalActivity(state, input.sourceNodeId, 'link', `Linked dependency: ${input.label}.`);
  });
}

export async function createScreen(specMapId: string, nodeId: string, input: CreateScreenInput) {
  const currentState = await getSpecMapState(specMapId);
  const targetNode = currentState.nodes.find((node) => node.id === nodeId);

  if (!targetNode) {
    throw new Error('Node not found.');
  }

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('screens').insert({
        spec_map_id: specMapId,
        version_id: currentState.specMap.activeVersionId,
        node_id: nodeId,
        label: input.label,
        status: input.status,
        priority: input.priority,
        owner_name: input.owner,
        description: input.description,
        sort_order: targetNode.childScreens.length,
      });
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        nodeId,
        'edit',
        `Added screen: ${input.label}.`
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const node = state.nodes.find((item) => item.id === nodeId);

    if (!node) {
      throw new Error('Node not found.');
    }

    node.childScreens = [
      ...node.childScreens,
      {
        id: makeId('screen'),
        ...input,
        order: node.childScreens.length,
      },
    ];
    addLocalActivity(state, nodeId, 'edit', `Added screen: ${input.label}.`);
  });
}

export async function updateScreen(
  specMapId: string,
  nodeId: string,
  screenId: string,
  input: CreateScreenInput
) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase
        .from('screens')
        .update({
          label: input.label,
          status: input.status,
          priority: input.priority,
          owner_name: input.owner,
          description: input.description,
          updated_at: nowIso(),
        })
        .eq('id', screenId);
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        nodeId,
        'edit',
        `Updated screen: ${input.label}.`
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const node = state.nodes.find((item) => item.id === nodeId);

    if (!node) {
      throw new Error('Node not found.');
    }

    node.childScreens = node.childScreens.map((screen) =>
      screen.id === screenId ? { ...screen, ...input } : screen
    );
    addLocalActivity(state, nodeId, 'edit', `Updated screen: ${input.label}.`);
  });
}

export async function deleteScreen(specMapId: string, nodeId: string, screenId: string) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('screens').delete().eq('id', screenId);
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        nodeId,
        'edit',
        'Deleted a screen.'
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const node = state.nodes.find((item) => item.id === nodeId);

    if (!node) {
      throw new Error('Node not found.');
    }

    node.childScreens = node.childScreens.filter((screen) => screen.id !== screenId);
    addLocalActivity(state, nodeId, 'edit', 'Deleted a screen.');
  });
}

export async function flagNodeForReview(specMapId: string, nodeId: string) {
  const currentState = await getSpecMapState(specMapId);
  const node = currentState.nodes.find((item) => item.id === nodeId);
  const title = node ? `Review requested for ${node.title}` : 'Review requested';

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('notifications').insert({
        spec_map_id: specMapId,
        node_id: nodeId,
        title,
        body: 'This node was flagged for review from the inspector.',
        type: 'review',
      });
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        nodeId,
        'flag',
        'Flagged for review.'
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    state.notifications = [
      {
        id: makeId('notification'),
        title,
        body: 'This node was flagged for review from the inspector.',
        type: 'review',
        read: false,
        nodeId,
        createdAt: nowIso(),
      },
      ...state.notifications,
    ];
    addLocalActivity(state, nodeId, 'flag', 'Flagged for review.');
  });
}

export async function duplicateNode(specMapId: string, nodeId: string) {
  const currentState = await getSpecMapState(specMapId);
  const node = currentState.nodes.find((item) => item.id === nodeId);

  if (!node) {
    throw new Error('Node not found.');
  }

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      const { data: newNode, error } = await supabase
        .from('nodes')
        .insert({
          spec_map_id: specMapId,
          version_id: currentState.specMap.activeVersionId,
          lane_id: node.lane,
          title: `${node.title} Copy`,
          description: node.description,
          category: node.category,
          status: 'Draft',
          priority: node.priority,
          owner_name: node.owner,
          notes: node.notes,
          related_screens: node.relatedScreens,
          position_x: (node.positionX ?? 0) + 24,
          position_y: (node.positionY ?? 0) + 24,
          sort_order: currentState.nodes.length,
        })
        .select()
        .single();

      if (error || !newNode) {
        throw error ?? new Error('Could not duplicate node.');
      }

      await Promise.all(
        node.childScreens.map((screen) =>
          supabase.from('screens').insert({
            spec_map_id: specMapId,
            version_id: currentState.specMap.activeVersionId,
            node_id: newNode.id,
            label: screen.label,
            status: screen.status,
            priority: screen.priority,
            owner_name: screen.owner,
            description: screen.description,
            sort_order: screen.order ?? 0,
          })
        )
      );
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        newNode.id,
        'edit',
        `Duplicated from ${node.title}.`
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const copy: NodeData = {
      ...node,
      id: makeId('node'),
      title: `${node.title} Copy`,
      status: 'Draft',
      positionX: (node.positionX ?? 0) + 24,
      positionY: (node.positionY ?? 0) + 24,
      sortOrder: state.nodes.length,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      childScreens: node.childScreens.map((screen, index) => ({
        ...screen,
        id: makeId('screen'),
        order: index,
      })),
      dependencies: [],
      activity: [],
    };
    state.nodes = [...state.nodes, copy];
    addLocalActivity(state, copy.id, 'edit', `Duplicated from ${node.title}.`);
  });
}

export async function archiveNode(specMapId: string, nodeId: string) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase
        .from('nodes')
        .update({ archived_at: nowIso(), status: 'Deprecated', updated_at: nowIso() })
        .eq('id', nodeId);
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        nodeId,
        'status',
        'Archived node.'
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    const node = state.nodes.find((item) => item.id === nodeId);

    if (!node) {
      throw new Error('Node not found.');
    }

    node.archivedAt = nowIso();
    node.status = 'Deprecated';
    addLocalActivity(state, nodeId, 'status', 'Archived node.');
  });
}

export async function deleteNode(specMapId: string, nodeId: string) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('nodes').delete().eq('id', nodeId);
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    state.nodes = state.nodes.filter((node) => node.id !== nodeId);
    state.nodes = state.nodes.map((node) => ({
      ...node,
      dependencies: node.dependencies.filter((dependency) => dependency.targetNodeId !== nodeId),
    }));
  });
}

export async function createShareLink(specMapId: string) {
  const currentState = await getSpecMapState(specMapId);
  const token = Math.random().toString(36).slice(2, 10);
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      const { data } = await supabase.auth.getUser();
      const { data: share, error } = await supabase
        .from('share_links')
        .insert({
          spec_map_id: specMapId,
          token,
          permission: 'view',
          created_by: data.user?.id ?? null,
        })
        .select()
        .single();

      if (error || !share) {
        throw error ?? new Error('Could not create share link.');
      }

      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        null,
        'link',
        'Created a read-only share link.'
      );

      return {
        state: await getSpecMapState(specMapId),
        shareLink: {
          id: share.id,
          token: share.token,
          permission: share.permission as ShareLinkData['permission'],
          createdAt: share.created_at,
          url: `${currentOrigin}/share/${share.token}`,
        },
      };
    }
  }

  const shareLink: ShareLinkData = {
    id: makeId('share'),
    token,
    permission: 'view',
    createdAt: nowIso(),
    url: `${currentOrigin}/share/${token}`,
  };
  const nextState = mutateLocalState((state) => {
    state.shareLinks = [shareLink, ...state.shareLinks];
    addLocalActivity(state, null, 'link', 'Created a read-only share link.');
  });

  return { state: nextState, shareLink };
}

export function buildExportPayload(state: SpecMapState): SpecMapExport {
  return {
    specMap: state.specMap,
    versions: state.versions,
    lanes: state.lanes,
    nodes: state.nodes,
    comments: state.comments,
    exportedAt: nowIso(),
    source: state.source,
  };
}

export async function markNotificationsRead(specMapId: string) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase.from('notifications').update({ read: true }).eq('spec_map_id', specMapId);
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    state.notifications = state.notifications.map((notification) => ({
      ...notification,
      read: true,
    }));
  });
}

export async function updateSpecMapMetadata(
  specMapId: string,
  patch: SpecMapMetadataPatch
) {
  const currentState = await getSpecMapState(specMapId);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      await supabase
        .from('spec_maps')
        .update({
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.description !== undefined ? { description: patch.description } : {}),
          ...(patch.status !== undefined ? { status: patch.status } : {}),
          ...(patch.clientName !== undefined ? { client_name: patch.clientName } : {}),
          ...(patch.logoUrl !== undefined ? { logo_url: patch.logoUrl } : {}),
          ...(patch.logoAlt !== undefined ? { logo_alt: patch.logoAlt } : {}),
          updated_at: nowIso(),
        })
        .eq('id', specMapId);
      await insertSupabaseActivity(
        specMapId,
        currentState.specMap.activeVersionId,
        null,
        'edit',
        'Updated map metadata.'
      );
      return getSpecMapState(specMapId);
    }
  }

  return mutateLocalState((state) => {
    state.specMap = {
      ...state.specMap,
      ...patch,
      updatedAt: nowIso(),
    };
    addLocalActivity(state, null, 'edit', 'Updated map metadata.');
  });
}

export async function uploadSpecMapLogo(specMapId: string, file: File) {
  validateLogoFile(file);
  const currentState = await getSpecMapState(specMapId);
  const logoAlt = logoAltFromFile(file, currentState);

  if (currentState.source === 'supabase') {
    const supabase = getSupabaseBrowserClient();

    if (supabase) {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'png';
      const path = `${specMapId}/${Date.now()}-${makeId('logo')}.${extension}`;
      const { error } = await supabase.storage.from(LOGO_STORAGE_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: true,
      });

      if (error) {
        throw error;
      }

      const { data } = supabase.storage.from(LOGO_STORAGE_BUCKET).getPublicUrl(path);
      return updateSpecMapMetadata(specMapId, {
        logoUrl: data.publicUrl,
        logoAlt,
        clientName: currentState.specMap.clientName || logoAlt,
      });
    }
  }

  const logoUrl = await readFileAsDataUrl(file);
  return updateSpecMapMetadata(specMapId, {
    logoUrl,
    logoAlt,
    clientName: currentState.specMap.clientName || logoAlt,
  });
}

export async function duplicateSpecMap(specMapId: string) {
  const state = await getSpecMapState(specMapId);

  if (state.source === 'supabase') {
    throw new Error('Map duplication should be run as a backend job for Supabase projects.');
  }

  return mutateLocalState((draft) => {
    draft.specMap = {
      ...draft.specMap,
      id: `${draft.specMap.id}-copy`,
      title: `${draft.specMap.title} Copy`,
      status: 'Draft',
      updatedAt: nowIso(),
    };
    draft.nodes = draft.nodes.map((node, index) => ({
      ...node,
      id: `${node.id}-copy`,
      sortOrder: index,
      updatedAt: nowIso(),
      activity: [],
    }));
    draft.shareLinks = [];
    draft.comments = [];
    draft.notifications = [];
  });
}

export async function importSpecMapPayload(payload: SpecMapExport) {
  const seed = buildSeedState('local');
  const nextState: SpecMapState = finalizeState({
    ...seed,
    specMap: {
      ...normalizeSpecMapData(payload.specMap ?? {}, seed.specMap),
      id: payload.specMap?.id || DEFAULT_SPEC_MAP_ID,
      updatedAt: nowIso(),
    },
    versions: payload.versions?.length ? payload.versions : seed.versions,
    lanes: payload.lanes?.length ? payload.lanes : seed.lanes,
    nodes: payload.nodes?.length ? payload.nodes : seed.nodes,
    comments: payload.comments ?? [],
    shareLinks: [],
    notifications: [],
    source: 'local',
  });

  writeLocalState(nextState);
  return nextState;
}

export function subscribeToSpecMap(
  specMapId: string,
  onChange: () => void,
  onPresence?: (collaborators: CollaboratorData[]) => void
) {
  const supabase = getSupabaseBrowserClient();

  if (!supabase) {
    return () => undefined;
  }

  const channel = supabase
    .channel(`spec-map:${specMapId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'spec_maps', filter: `id=eq.${specMapId}` },
      onChange
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'lanes', filter: `spec_map_id=eq.${specMapId}` },
      onChange
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'nodes', filter: `spec_map_id=eq.${specMapId}` },
      onChange
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'screens', filter: `spec_map_id=eq.${specMapId}` },
      onChange
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'dependencies',
        filter: `spec_map_id=eq.${specMapId}`,
      },
      onChange
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'comments', filter: `spec_map_id=eq.${specMapId}` },
      onChange
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'activity_events',
        filter: `spec_map_id=eq.${specMapId}`,
      },
      onChange
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `spec_map_id=eq.${specMapId}`,
      },
      onChange
    )
    .on('presence', { event: 'sync' }, () => {
      const presence = channel.presenceState();
      const active = Object.values(presence).flatMap((entries) =>
        entries.map((entry) => entry as { user_id?: string; name?: string; initials?: string })
      );

      if (onPresence && active.length > 0) {
        onPresence(
          active.map((entry, index) => ({
            id: entry.user_id ?? `presence-${index}`,
            name: entry.name ?? 'Collaborator',
            initials: entry.initials ?? 'CO',
            color: ['#0F9B8E', '#3B82F6', '#A78BFA', '#F59E0B'][index % 4],
            role: 'Collaborator',
            active: true,
          }))
        );
      }
    })
    .subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({
          user_id: CURRENT_USER.id,
          name: CURRENT_USER.name,
          initials: TEAM_ROSTER.engineer2.initials,
          online_at: nowIso(),
        });
      }
    });

  return () => {
    void supabase.removeChannel(channel);
  };
}
